import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import {
  GetRecurringTransactionsDocument,
  type GetRecurringTransactionsQuery,
  type GetRecurringTransactionsQueryVariables,
} from "@/src/gql/graphql";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import { mapRecurringTransactionEdges } from "@/lib/types/quickbooks/recurring-transactions";
import {
  useCreateRecurringTransaction,
  useDeleteRecurringTransaction,
  useUpdateRecurringTransaction,
} from "./recurring-transactions-mutations";
import { useRecurringTransaction } from "./recurring-transactions-queries";

export function useRecurringTransactionHooks(): EntityHooks<RecurringTransaction> {
  return {
    usePaginated: () => ({
      data: undefined,
      isLoading: false,
      error: null,
      refetch: () => {},
    }),

    useInfinite: () => ({
      data: undefined,
      isLoading: false,
      error: null,
      refetch: () => {},
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => {},
    }),

    useSingle: (id: string) => {
      const result = useRecurringTransaction(id);
      return {
        data: result.data || undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      };
    },

    useCreate: () => {
      const { createTransaction, loading } = useCreateRecurringTransaction();
      return {
        mutateAsync: async (data: Partial<RecurringTransaction> & { id?: string }) => {
          const result = await createTransaction({
            customer_id: data.customer_id as string,
            item: data.item as string,
            description: data.description,
            amount: data.amount as number,
            quantity: data.quantity !== undefined ? Number(data.quantity) : undefined,
            quickbooks_realm_id: data.quickbooks_realm_id,
          });
          return result as RecurringTransaction;
        },
        isPending: loading,
      };
    },

    useUpdate: () => {
      const { updateTransaction, loading } = useUpdateRecurringTransaction();
      return {
        mutateAsync: async (data: Partial<RecurringTransaction> & { id: string }) => {
          const result = await updateTransaction(data.id, data);
          return result as RecurringTransaction;
        },
        isPending: loading,
      };
    },

    useDelete: () => {
      const { deleteTransaction, loading } = useDeleteRecurringTransaction();
      return {
        mutateAsync: async (id: string) => {
          await deleteTransaction(id);
          return id;
        },
        isPending: loading,
      };
    },
  };
}

export function useRecurringTransactionHooksForPage(): EntityHooks<RecurringTransaction> {
  return {
    queryDocument: GetRecurringTransactionsDocument,
    usePaginated: (params?: Partial<GetRecurringTransactionsQueryVariables>) => {
      const result = useQuery<GetRecurringTransactionsQuery>(GetRecurringTransactionsDocument, {
        variables: params,
        errorPolicy: "all",
        notifyOnNetworkStatusChange: true,
        // Match the canonical list-query policy (see use-paginated-collection.ts):
        // cache-first lets a filtered list render a stale/empty *Collection until
        // a manual refetch. This hook keeps its bespoke edge-mapper + useMemo.
        fetchPolicy: "cache-and-network",
      });

      const transactions = useMemo(
        () => mapRecurringTransactionEdges(result.data?.recurring_transactionsCollection?.edges),
        [result.data]
      );

      return {
        data: result.data
          ? {
              data: transactions,
              totalCount: result.data.recurring_transactionsCollection?.totalCount || 0,
              pageInfo: result.data.recurring_transactionsCollection?.pageInfo,
            }
          : undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      };
    },

    useInfinite: () => ({
      data: undefined,
      isLoading: false,
      error: null,
      refetch: () => {},
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => {},
    }),

    useSingle: (id: string, options?: { enabled?: boolean }) => {
      const result = useRecurringTransaction(id);
      return {
        data: result.data || undefined,
        isLoading: result.loading,
        error: result.error || null,
        refetch: result.refetch,
      };
    },

    useCreate: () => {
      const { createTransaction, loading } = useCreateRecurringTransaction();
      return {
        mutateAsync: async (data: Partial<RecurringTransaction> & { id?: string }) => {
          const result = await createTransaction({
            customer_id: data.customer_id as string,
            item: data.item as string,
            description: data.description,
            amount: data.amount as number,
            quantity: data.quantity !== undefined ? Number(data.quantity) : undefined,
            quickbooks_realm_id: data.quickbooks_realm_id,
          });
          return result as RecurringTransaction;
        },
        isPending: loading,
      };
    },

    useUpdate: () => {
      const { updateTransaction, loading } = useUpdateRecurringTransaction();
      return {
        mutateAsync: async (data: Partial<RecurringTransaction> & { id: string }) => {
          const result = await updateTransaction(data.id, data);
          return result as RecurringTransaction;
        },
        isPending: loading,
      };
    },

    useDelete: () => {
      const { deleteTransaction, loading } = useDeleteRecurringTransaction();
      return {
        mutateAsync: async (id: string) => {
          await deleteTransaction(id);
          return id;
        },
        isPending: loading,
      };
    },
  };
}
